// P-AUTH-GATE-AND-ONBOARDING-DRAFT (closes tracker #40, QA-AUDIT-2026-06-01
// Area 1 P1: "Logged in but still onboarding on cloud-fetch failure").
//
// THE BUG: the auth-gate ladder in App.jsx derives `needsOnboarding` from
// LOCAL state only (`!cloudStudent && !progress.onboardingComplete`).
// Cross-device hydration (PR #69) repopulates that local state from the
// cloud — but ONLY when `fetchStudents` SUCCEEDS. On a timeout/offline boot
// the fetch returns [], hydration no-ops, and a PROVISIONED returning parent
// is funnelled into re-onboarding (a parent-trust violation, same family as
// PR #78's cross-user flash). The empty list also defeats lookup-before-create
// dedup → duplicate-student risk.
//
// THE FIX (this module is the pure decision core): when the initial
// students-fetch FAILED (cloudError set — see useCloudSync's fetch-only
// sentinel) AND we can prove the signed-in user is a RETURNING provisioned
// parent, HOLD them at a recoverable retry surface instead of re-onboarding.
//
// WHY "prove returning" rather than "prove brand-new" (the inverted heuristic,
// locked with Alex): the wrong-guess failure modes are asymmetric.
//   • Guess "returning" wrongly  → a brand-new user is BLOCKED from onboarding
//     (a NEW bug, strictly worse than the one we're fixing).
//   • Guess "brand-new" wrongly  → a returning parent re-onboards (exactly the
//     status quo — no regression).
// So we only HOLD on POSITIVE proof of a returning account; every ambiguous
// case (no created_at, recent created_at, the ?dev=1 e2e user) falls through
// to onboarding exactly as today.

// THRESHOLD — minimum auth-account age for "this is a returning parent, not a
// fresh signup." Calibration target (NOT a cited constant): a brand-new OAuth
// account is seconds old at first gate-evaluation, but slow OAuth on mobile
// networks regularly pushes the boot fetch (and thus this evaluation) past 60s,
// so a 60s cutoff would misclassify some genuinely returning parents on a
// flaky first load as "brand new" and re-onboard them. 5 minutes is a generous
// margin that still excludes any plausible same-sitting brand-new signup.
// Tune against the onboarding-funnel instrumentation named in
// onboarding-science-deep-research.md (parent-survey completion, time-to-first-
// child-action, D1/D7 return by variant).
export const RETURNING_PARENT_MIN_ACCOUNT_AGE_MS = 5 * 60 * 1000;

/**
 * True only when we can POSITIVELY prove the signed-in user is a returning
 * provisioned parent: their auth account exists and is older than the
 * threshold. Absent / unparseable / recent created_at → false (treat as
 * possibly-brand-new and let them onboard). The ?dev=1 bypass user has no
 * created_at, so it returns false here → dev/e2e onboarding is unaffected.
 *
 * @param {{created_at?: string}|null|undefined} user - the Supabase auth user.
 * @param {number} [now=Date.now()] - injectable clock for tests.
 * @param {number} [thresholdMs=RETURNING_PARENT_MIN_ACCOUNT_AGE_MS]
 * @returns {boolean}
 */
export function isReturningProvisionedParent(
  user,
  now = Date.now(),
  thresholdMs = RETURNING_PARENT_MIN_ACCOUNT_AGE_MS,
) {
  if (!user || !user.created_at) return false;
  const createdMs = Date.parse(user.created_at);
  if (!Number.isFinite(createdMs)) return false;
  // Clock-skew guard: a created_at in the FUTURE (device clock behind the
  // server) yields a negative age → not provably returning → false (onboard).
  const ageMs = now - createdMs;
  return ageMs > thresholdMs;
}

/**
 * The gate decision: HOLD the user at the cloud-error retry surface (instead
 * of re-onboarding) iff the initial students-fetch failed AND the user is a
 * provably-returning provisioned parent.
 *
 * Caller (App.jsx) only consults this when `needsOnboarding` is already true,
 * so a returning parent whose local state IS hydrated never reaches here.
 *
 * @param {object} args
 * @param {Error|null|undefined} args.cloudError - useCloudSync's fetch-only
 *   sentinel (null when the initial students-fetch succeeded).
 * @param {{created_at?: string}|null|undefined} args.user
 * @param {number} [args.now=Date.now()]
 * @param {number} [args.thresholdMs=RETURNING_PARENT_MIN_ACCOUNT_AGE_MS]
 * @returns {boolean}
 */
export function shouldHoldForCloudError({
  cloudError,
  user,
  now = Date.now(),
  thresholdMs = RETURNING_PARENT_MIN_ACCOUNT_AGE_MS,
}) {
  if (!cloudError) return false;
  return isReturningProvisionedParent(user, now, thresholdMs);
}

// MA-394 — a SECOND hold class, distinct from the one above: the students
// fetch did not fail, it SUCCEEDED with zero rows (e.g. both of a parent's
// students are is_active=false, per the MA-392/393 retention-purge incident).
// `cloudError` stays null on a successful-but-empty fetch, so
// shouldHoldForCloudError never fires here — without this second guard, a
// returning parent whose cloud students were deactivated (or a fresh-device
// sign-in that arrives before any local pointer exists) is funnelled straight
// into onboarding/signup, which reads as "create a new account" (the founder's
// literal 2026-07-27 report). Same inverted-heuristic contract as
// shouldHoldForCloudError: HOLD only on positive proof of a returning account;
// every ambiguous case (brand-new, ?dev=1, unsettled fetch, nonzero students)
// falls through to onboarding exactly as today.
/**
 * @param {object} args
 * @param {Error|null|undefined} args.cloudError - null when the fetch
 *   succeeded (even with zero rows) — this guard is only for that case; a
 *   real fetch failure is shouldHoldForCloudError's job.
 * @param {boolean} args.cloudLoading - true while the initial fetch is still
 *   in flight. Defensive: the auth-gate ladder already holds on cloudLoading
 *   before reaching this check, but a caller with an unsettled fetch must
 *   never get a false verdict either way.
 * @param {Array<object>|null|undefined} args.students - the fetched (already
 *   is_active=true filtered) students list.
 * @param {{created_at?: string}|null|undefined} args.user
 * @param {boolean|number|null|undefined} [args.hasAnyOwnedStudent] - MA-629 —
 *   tri-state positive proof of emptiness from fetchStudents' owned-student
 *   probe: `true`/a positive count (owns rows, active or not), `false`/`0`
 *   (proven to own NONE), or `null`/absent/anything else (unknown). Only a
 *   strict `false` or `0` can stop a hold; every other shape — including a
 *   value a future producer gets wrong — keeps today's behaviour.
 * @param {number} [args.now=Date.now()]
 * @param {number} [args.thresholdMs=RETURNING_PARENT_MIN_ACCOUNT_AGE_MS]
 * @returns {boolean}
 */
export function shouldShowZeroActiveRecovery({
  cloudError,
  cloudLoading,
  students,
  user,
  hasAnyOwnedStudent,
  now = Date.now(),
  thresholdMs = RETURNING_PARENT_MIN_ACCOUNT_AGE_MS,
}) {
  if (cloudError) return false;
  if (cloudLoading) return false;
  if ((students?.length ?? 0) > 0) return false;
  // MA-629 §3 — Positive proof of emptiness — and ONLY that. Any other value
  // (absent, null, NaN, a string, a Promise, a positive count) keeps today's
  // behaviour.
  if (hasAnyOwnedStudent === 0 || hasAnyOwnedStudent === false) return false;
  return isReturningProvisionedParent(user, now, thresholdMs);
}

// MA-629 §3b — the App.jsx CONSUMPTION contract, extracted verbatim rather
// than inlined at the call site. Same MA-582 precedent as bootGateGuard.js's
// `shouldHoldForBoot` (`bootGateGuard.js:1-11`: "the expression itself moves
// here, verbatim, and App.jsx calls it instead of inlining it"): with
// `sourceTextAssertionRatchet.mjs` at its exact cap, no new COUNTED pin can
// read App.jsx, and no React render harness exists in this repo — so an
// inline `zeroActiveRecovery !== false` at the call site is pinned by
// NOTHING. Measured: a later edit to a bare `&& zeroActiveRecovery` shipped
// green through every suite in the repo when left inline. Naming this
// function gives an ordinary, executed unit test a seam to bite on instead —
// `!== false` is load-bearing, not defensive noise: it is the ONLY value
// that stops the hold, so a missing/renamed `zeroActiveRecovery` (which
// reaches App.jsx's destructure as `undefined` — `checkJs: false`,
// `noUnusedLocals: false` catch nothing there) still HOLDS
// (`undefined !== false` is `true`), rather than a bare truthiness check
// reading `undefined` as "no hold" and silently deleting MA-394's entire
// hold class.
export function shouldHoldZeroActive(zeroActiveRecovery) {
  return zeroActiveRecovery !== false;
}

// MA-394 (b) — the DEFERRED sub-clause: a two-way /onboarding redirect. The
// entry direction (OnboardingRedirector in App.jsx) forces a parent INTO
// /onboarding when needsOnboarding flips true off-route. Nothing forced them
// back OUT when cross-device hydration (crossDeviceHydration.js) settles
// AFTER they already landed on /onboarding (hydration runs in an effect, one
// render after the gate ladder first evaluates on a fresh device — see the
// `pendingCloudHydration` comment in App.jsx). A first attempt navigated
// imperatively from INSIDE the hydration effect and was dropped after a live
// e2e repro showed it oscillating (the effect's navigate raced the "/"
// front-door's own `beforeLoad` redirect and the gate ladder's next render,
// cycling /onboarding → / → /onboarding → /play → /onboarding).
//
// This is the router-transition-safe replacement: a pure, RENDER-TIME
// predicate — exactly the same shape as OnboardingRedirector's own entry
// condition (`needsOnboarding && !pathname.startsWith("/onboarding")`), just
// mirrored. Because it derives from already-committed render state (not an
// imperative call buried inside the hydration effect), it composes with
// React's own render cycle instead of racing it: once `needsOnboarding` and
// `pathname` both settle post-hydration, the predicate flips true for
// exactly one render, the caller's one-shot redirector effect fires once,
// and the resulting pathname change makes the predicate false again on its
// own — no separate latch required, same self-clearing mechanism the entry
// direction already relies on.
//
// Scoped to `hydratedThisSession` (not "any onboardingComplete flip") on
// purpose: the NORMAL onboarding-completion flow already navigates itself
// away from /onboarding (onboarding.parent-results.tsx → `/play`, right
// after handleOnboardingComplete flips onboardingComplete true) — a general
// "!needsOnboarding && on /onboarding" check would double-navigate against
// that in-flight transition and reintroduce exactly the race this predicate
// is built to avoid. Gating on the hydration latch confines this to the one
// scenario named in the item: a parent stranded on /onboarding by hydration
// that completed after they were already routed there.
/**
 * @param {object} args
 * @param {boolean} args.hydratedThisSession - cross-device hydration's
 *   session latch (`hasHydratedThisSessionRef.current` in App.jsx) — true
 *   only after `hydrateLocalFromCloud` actually populated the local registry
 *   this session.
 * @param {boolean} args.needsOnboarding - the gate ladder's own derivation
 *   (`!cloudStudent && !progress.onboardingComplete`).
 * @param {string|null|undefined} args.pathname - `location.pathname`.
 * @returns {boolean}
 */
export function shouldExitOnboardingAfterHydration({
  hydratedThisSession,
  needsOnboarding,
  pathname,
}) {
  if (!hydratedThisSession) return false;
  if (needsOnboarding) return false;
  return typeof pathname === "string" && pathname.startsWith("/onboarding");
}
